// src/components/Sidebar.js

import React, { useMemo } from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

import saffronLogo from "../assets/Original Rectangle Logo.png";

/* ===================================================== */
/* Styles                                                */
/* ===================================================== */

const base =
  "flex items-center gap-3 px-4 py-2 rounded-md text-sm font-medium transition-all duration-150";

const active =
  "bg-gray-100 text-gray-900 shadow-sm font-semibold";

const idle =
  "text-gray-600 hover:bg-gray-100 hover:text-gray-900";

/* ===================================================== */
/* Component                                             */
/* ===================================================== */

export default function Sidebar({ onNavigate }) {
  const {
    user,
    logout,
    companies = [],
    companyId,
    activeCompany,
    membership,
  } = useAuth();

  /* ===================================================== */
  /* Derived                                               */
  /* ===================================================== */

  const currentCompany = useMemo(() => {
    return (
      activeCompany ||
      companies.find((c) => c._id === companyId) ||
      null
    );
  }, [activeCompany, companies, companyId]);

  const isAdmin = membership?.role === "admin";

  /* ===================================================== */
  /* Nav Item                                              */
  /* ===================================================== */

  const NavItem = ({ to, label, icon }) => (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `${base} ${isActive ? active : idle}`
      }
      onClick={onNavigate}
    >
      <span className="text-lg">{icon}</span>
      <span>{label}</span>
    </NavLink>
  );

  /* ===================================================== */
  /* Logout Handler                                        */
  /* ===================================================== */

  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      console.error("Logout failed:", err.message);
    }
  };

  /* ===================================================== */
  /* UI                                                    */
  /* ===================================================== */

  return (
    <aside className="h-full flex flex-col bg-white border-r shadow-sm">

      {/* ================================================= */}
      {/* Logo                                              */}
      {/* ================================================= */}

      <div className="h-16 border-b flex items-center px-4">
        <img
          src={saffronLogo}
          alt="HeadwaySpot"
          className="h-8 w-auto object-contain"
        />
      </div>

      {/* ================================================= */}
      {/* Active Company                                    */}
      {/* ================================================= */}

      {currentCompany && (
        <div className="px-4 py-2 border-b">
          <div className="text-xs text-gray-500">Workspace</div>
          <div className="text-sm font-semibold text-gray-900 truncate">
            {currentCompany.name}
          </div>
        </div>
      )}

      {/* ================================================= */}
      {/* Navigation                                        */}
      {/* ================================================= */}

      <nav className="flex-1 p-4 space-y-1">

        {/* Core */}
        <NavItem to="/dashboard" label="Dashboard" icon="🏠" />
        <NavItem to="/projects" label="Projects" icon="📁" />
        <NavItem to="/billing" label="Billing" icon="🧾" />
        <NavItem to="/notifications" label="Notifications" icon="🔔" />

        {/* ================= Admin ================= */}
        {isAdmin && (
          <>
            <div className="mt-4 text-xs font-semibold text-gray-500 uppercase tracking-wide px-2">
              Admin
            </div>

            <NavItem to="/teams" label="Teams" icon="👥" />

            {/* 🔥 NEW: Contractors */}
            <NavItem
              to="/contractors"
              label="Contractors"
              icon="🤝"
            />

            <div className="mt-2 ml-2 space-y-1">

              <NavItem
                to="/reports"
                label="Project Reports"
                icon="📊"
              />

              <NavItem
                to="/user-reports"
                label="User Reports"
                icon="👤"
              />

              <NavItem
                to="/team-reports"
                label="Team Reports"
                icon="👥"
              />

            </div>

            <NavItem
              to="/integrations"
              label="Integrations"
              icon="🔗"
            />
          </>
        )}
      </nav>

      {/* ================================================= */}
      {/* User Footer                                       */}
      {/* ================================================= */}

      <div className="p-4 border-t flex items-center justify-between gap-2">

        <div className="flex flex-col min-w-0">
          <span className="text-sm font-medium text-gray-900 truncate">
            {user?.name || "User"}
          </span>

          <span className="text-xs text-gray-500 truncate">
            {user?.email}
          </span>
        </div>

        <button
          type="button"
          onClick={handleLogout}
          className="text-red-600 hover:text-red-700 text-sm font-medium whitespace-nowrap"
        >
          Log out
        </button>

      </div>

    </aside>
  );
}